export interface RazorpaySubscriptionResponse {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}

export interface RazorpayInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: (response: { error: { description: string } }) => void) => void;
}

export type RazorpayConstructor = new (options: Record<string, unknown>) => RazorpayInstance;

let razorpayScriptPromise: Promise<boolean> | null = null;
const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function getRazorpayConstructor(): RazorpayConstructor | null {
  if (typeof window === 'undefined') return null;
  return (window as Window & { Razorpay?: RazorpayConstructor }).Razorpay ?? null;
}

export function getRazorpay(): RazorpayConstructor | null {
  return getRazorpayConstructor();
}

export function loadRazorpayCheckoutScript(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (getRazorpayConstructor()) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      if (!ok) razorpayScriptPromise = null;
      resolve(ok);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${RAZORPAY_CHECKOUT_SRC}"]`,
    );

    // A script tag is not proof that checkout.js initialized successfully.
    // If it loaded/failed before these listeners were attached, reusing it
    // leaves every later checkout attempt waiting on events that cannot fire.
    // The module-level promise already keeps active calls single-flight, so a
    // missing constructor here means the existing tag is safe to replace.
    existing?.remove();
    const script = document.createElement('script');

    script.addEventListener('load', () => finish(Boolean(getRazorpayConstructor())), { once: true });
    script.addEventListener('error', () => finish(false), { once: true });

    script.src = RAZORPAY_CHECKOUT_SRC;
    script.async = true;
    document.head.appendChild(script);

    window.setTimeout(() => finish(Boolean(getRazorpayConstructor())), 5000);
  });

  return razorpayScriptPromise;
}
